import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Archive, Copy, Eye, FileText, Image, Layers, LockKeyhole, Plus, Save, Search, Upload } from 'lucide-react';
import { createEmailTemplate, deleteEmailTemplate, getEmailTemplates, updateEmailTemplate } from '../../../data/api';
import { toast } from 'sonner';

const BLOCKS = [
  {
    label: 'Luxury Header',
    html: '<table role="presentation" width="100%" style="padding:28px 24px;text-align:center;background:#fffaf0;border:1px solid #ead8a6;"><tr><td><div style="font-family:Georgia,serif;font-size:34px;color:#b98518;">80</div><div style="letter-spacing:4px;color:#8a6b2c;">YEARS OF CONNECTION</div></td></tr></table>',
  },
  {
    label: 'Event Detail',
    html: '<p style="font-family:Arial,sans-serif;font-size:15px;line-height:1.7;color:#2d2a24;">We would be honored to have you with us for a celebration of love, family, and cherished memories.</p>',
  },
  {
    label: 'Call to Action',
    html: '<p style="text-align:center;margin:28px 0;"><a href="{{rsvp_link}}" style="display:inline-block;padding:14px 28px;background:#b98518;color:#ffffff;text-decoration:none;border-radius:999px;font-weight:700;">View Invitation</a></p>',
  },
  {
    label: 'Signature',
    html: '<p style="font-family:Georgia,serif;font-size:18px;color:#2d2a24;">With love,<br>The Valenciano Family</p>',
  },
];

const emptyDraft = {
  id: '',
  name: '',
  subject: '',
  template_type: 'custom',
  html_content: '<p>Hello {{first_name}},</p><p></p>',
  is_active: false,
};

function templateId(template) {
  return template?.id || template?._id || '';
}

function templateStatus(template) {
  if (template?.is_system_protected || template?.is_builtin || template?.is_live) return 'System Protected';
  return template?.is_active ? 'Active' : 'Draft';
}

function canEdit(template) {
  return !template?.is_system_protected && !template?.is_builtin && template?.can_edit !== false;
}

export default function TemplatesPanel() {
  const fileRef = useRef(null);
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState(emptyDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [imageUrl, setImageUrl] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');

  const selectedTemplate = useMemo(
    () => templates.find(template => templateId(template) === selectedId) || null,
    [templates, selectedId]
  );
  const editable = canEdit(selectedTemplate);
  const filteredTemplates = useMemo(() => {
    const query = templateSearch.trim().toLowerCase();
    if (!query) return templates;
    return templates.filter(template => [
      template.name,
      template.subject,
      template.template_type,
      templateStatus(template),
    ].join(' ').toLowerCase().includes(query));
  }, [templates, templateSearch]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await getEmailTemplates();
      setTemplates(Array.isArray(data) ? data : []);
      const first = Array.isArray(data) && data.length ? data[0] : null;
      if (!selectedId && first) {
        setSelectedId(templateId(first));
      }
    } catch (error) {
      console.error(error);
      toast.error('Templates could not be loaded');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedTemplate) {
      setDraft(emptyDraft);
      return;
    }
    setDraft({
      id: templateId(selectedTemplate),
      name: selectedTemplate.name || '',
      subject: selectedTemplate.subject || '',
      template_type: selectedTemplate.template_type || 'custom',
      html_content: selectedTemplate.html_content || '',
      is_active: Boolean(selectedTemplate.is_active && !selectedTemplate.is_system_protected),
    });
  }, [selectedTemplate]);

  const startNew = () => {
    setSelectedId('');
    setDraft({
      ...emptyDraft,
      name: 'New Communication Template',
      subject: 'A note from 80 Years of Connection',
    });
  };

  const duplicateSelected = () => {
    if (!selectedTemplate) return;
    setSelectedId('');
    setDraft({
      ...emptyDraft,
      name: `${selectedTemplate.name || 'Template'} Copy`,
      subject: selectedTemplate.subject || '',
      template_type: selectedTemplate.template_type === 'confirmation' ? 'custom' : (selectedTemplate.template_type || 'custom'),
      html_content: selectedTemplate.html_content || '',
    });
  };

  const updateDraft = (key, value) => {
    setDraft(prev => ({ ...prev, [key]: value }));
  };

  const insertBlock = (html) => {
    if (!editable && selectedTemplate) return;
    setDraft(prev => ({
      ...prev,
      html_content: `${prev.html_content || ''}\n\n${html}`,
    }));
  };

  const insertImage = () => {
    const cleanUrl = imageUrl.trim();
    if (!cleanUrl) return;
    insertBlock(`<p style="text-align:center;"><img src="${cleanUrl}" alt="" style="max-width:100%;border-radius:18px;" /></p>`);
    setImageUrl('');
  };

  const importFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const content = String(reader.result || '');
      setDraft(prev => ({
        ...prev,
        name: prev.name || file.name.replace(/\.[^.]+$/, ''),
        html_content: content,
      }));
      toast.success('Template content imported');
    };
    reader.onerror = () => toast.error('Could not import that file');
    reader.readAsText(file);
    event.target.value = '';
  };

  const saveTemplate = async (active) => {
    if (!draft.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (!draft.subject.trim()) {
      toast.error('Subject is required');
      return;
    }
    if (!draft.html_content.trim()) {
      toast.error('Template content is required');
      return;
    }
    if (selectedTemplate && !editable) {
      toast.error('System protected templates cannot be edited');
      return;
    }

    setSaving(true);
    const payload = {
      name: draft.name.trim(),
      subject: draft.subject.trim(),
      template_type: draft.template_type || 'custom',
      html_content: draft.html_content,
      is_active: active,
    };

    try {
      const saved = draft.id
        ? await updateEmailTemplate(draft.id, payload)
        : await createEmailTemplate(payload);
      await loadTemplates();
      setSelectedId(templateId(saved));
      toast.success(active ? 'Template saved as active' : 'Template saved as draft');
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Template could not be saved');
    } finally {
      setSaving(false);
    }
  };

  const archiveTemplate = async () => {
    if (!selectedTemplate || !editable) return;
    if (!window.confirm('Archive this template? Existing sent messages and snapshots will not change.')) return;
    try {
      await deleteEmailTemplate(templateId(selectedTemplate));
      setSelectedId('');
      await loadTemplates();
      toast.success('Template archived');
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Template could not be archived');
    }
  };

  return (
    <div className="communications-panel communications-templates" data-testid="communications-templates-panel">
      <div className="communications-panel__toolbar communications-studio-toolbar">
        <div>
          <h2>Templates</h2>
        </div>
        <div className="communications-toolbar-actions">
          <button type="button" className="communications-button communications-button--ghost" onClick={() => fileRef.current?.click()}>
            <Upload size={16} />
            Import HTML/TXT
          </button>
          <input ref={fileRef} type="file" accept=".html,.htm,.txt" onChange={importFile} hidden />
          <button type="button" className="communications-button communications-button--primary" onClick={startNew}>
            <Plus size={16} />
            New Template
          </button>
        </div>
      </div>

      <div className="communications-template-studio">
        <aside className="communications-template-library" aria-label="Template library">
          <div className="communications-template-library__header">
            <strong>Library</strong>
            <label className="communications-search communications-template-search">
              <Search size={15} />
              <input value={templateSearch} onChange={event => setTemplateSearch(event.target.value)} placeholder="Find templates" />
            </label>
          </div>
          {loading ? (
            <div className="communications-empty communications-empty--compact"><p>Loading templates...</p></div>
          ) : filteredTemplates.length ? filteredTemplates.map(template => {
            const id = templateId(template);
            const selected = id === selectedId;
            return (
              <button
                key={id}
                type="button"
                className={`communications-template-card ${selected ? 'communications-template-card--active' : ''}`}
                onClick={() => setSelectedId(id)}
              >
                <span className="communications-template-card__icon">
                  {template.is_system_protected || template.is_builtin ? <LockKeyhole size={16} /> : <FileText size={16} />}
                </span>
	                <span>
	                  <strong>{template.name || 'Untitled template'}</strong>
	                  <small>{template.subject || 'No subject set'}</small>
	                </span>
	                <em>{templateStatus(template)}</em>
	              </button>
	            );
	          }) : (
	            <div className="communications-empty communications-empty--compact"><p>No templates match that search.</p></div>
	          )}
	        </aside>

	        <section className="communications-template-editor" aria-label="Template editor">
	          {selectedTemplate && !editable && (
            <div className="communications-callout communications-callout--success">
              <LockKeyhole size={16} />
	              <span>{selectedTemplate.name} is System Protected. You can preview it, but editing and deletion are locked.</span>
	            </div>
	          )}

	          <div className="communications-template-statebar">
	            <span className={`communications-pill ${selectedTemplate && !editable ? 'communications-pill--warn' : draft.is_active ? 'communications-pill--success' : ''}`}>
	              {selectedTemplate && !editable ? 'System Protected' : draft.is_active ? 'Active Template' : 'Draft Template'}
	            </span>
	            <p>Template edits are saved separately from sent message snapshots.</p>
	          </div>

          <div className="communications-template-editor__grid">
            <label>
              <span>Name</span>
              <input value={draft.name} disabled={selectedTemplate && !editable} onChange={event => updateDraft('name', event.target.value)} />
            </label>
            <label>
              <span>Purpose</span>
              <select value={draft.template_type} disabled={selectedTemplate && !editable} onChange={event => updateDraft('template_type', event.target.value)}>
                <option value="invitation">Invitation</option>
                <option value="reminder">Reminder</option>
                <option value="follow_up">Follow-up</option>
                <option value="thank_you">Thank You</option>
                <option value="custom">Custom</option>
              </select>
            </label>
          </div>

          <label className="communications-template-editor__field">
            <span>Subject</span>
            <input value={draft.subject} disabled={selectedTemplate && !editable} onChange={event => updateDraft('subject', event.target.value)} />
          </label>

          <div className="communications-template-blocks">
            <div>
              <strong>Reusable Blocks</strong>
              <p>Add polished sections without rebuilding the template manually.</p>
            </div>
            <div className="communications-template-blocks__actions">
              {BLOCKS.map(block => (
                <button key={block.label} type="button" onClick={() => insertBlock(block.html)} disabled={selectedTemplate && !editable}>
                  <Layers size={14} />
                  {block.label}
                </button>
              ))}
            </div>
          </div>

          <div className="communications-template-image">
            <label>
              <span>Image URL</span>
              <input value={imageUrl} disabled={selectedTemplate && !editable} onChange={event => setImageUrl(event.target.value)} placeholder="https://..." />
            </label>
            <button type="button" className="communications-button communications-button--ghost" onClick={insertImage} disabled={(selectedTemplate && !editable) || !imageUrl.trim()}>
              <Image size={16} />
              Insert Image
            </button>
          </div>

	          <details className="communications-template-advanced">
	            <summary>Advanced HTML Editor</summary>
	            <label className="communications-template-editor__field">
	              <span>Email HTML</span>
	              <textarea value={draft.html_content} disabled={selectedTemplate && !editable} onChange={event => updateDraft('html_content', event.target.value)} rows={11} />
	            </label>
	          </details>

          <div className="communications-template-actions">
            <button type="button" className="communications-button communications-button--ghost" onClick={duplicateSelected} disabled={!selectedTemplate}>
              <Copy size={16} />
              Duplicate
            </button>
            <button type="button" className="communications-button communications-button--ghost" onClick={archiveTemplate} disabled={!selectedTemplate || !editable}>
              <Archive size={16} />
              Archive
            </button>
            <button type="button" className="communications-button communications-button--ghost" onClick={() => saveTemplate(false)} disabled={saving || (selectedTemplate && !editable)}>
              <Save size={16} />
              Save Draft
            </button>
            <button type="button" className="communications-button communications-button--primary" onClick={() => saveTemplate(true)} disabled={saving || (selectedTemplate && !editable)}>
              <Save size={16} />
              Save Active
            </button>
          </div>
        </section>

        <aside className="communications-template-preview" aria-label="Template preview">
          <div className="communications-template-preview__header">
            <div>
              <Eye size={16} />
              <strong>Visual Preview</strong>
            </div>
            <div className="communications-preview-toggle">
              <button type="button" className={previewMode === 'desktop' ? 'is-active' : ''} onClick={() => setPreviewMode('desktop')}>Desktop</button>
              <button type="button" className={previewMode === 'mobile' ? 'is-active' : ''} onClick={() => setPreviewMode('mobile')}>Mobile</button>
            </div>
          </div>
          <div className={`communications-template-preview__frame communications-template-preview__frame--${previewMode}`}>
            <iframe title="Template visual preview" srcDoc={draft.html_content || ''} sandbox="" />
          </div>
          <p className="communications-template-preview__note">
            Preview only. Send from the Send tab.
          </p>
        </aside>
      </div>
    </div>
  );
}
